class ProductComfortStories extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-comfort-stories-track]');
    this.controls = this.querySelector('.product-comfort-stories__controls');
    this.dotsWrap = this.querySelector('.product-comfort-stories__dots');
    this.prevButton = this.querySelector('[data-comfort-story-prev]');
    this.nextButton = this.querySelector('[data-comfort-story-next]');
    this.dots = [];
    if (!this.track) return;

    this.prevButton?.addEventListener('click', () => this.goToPage(this.currentPage() - 1));
    this.nextButton?.addEventListener('click', () => this.goToPage(this.currentPage() + 1));

    this.track.addEventListener('scroll', () => this.scheduleUpdate(), { passive: true });
    this.track.addEventListener('dragstart', (event) => event.preventDefault());
    this.bindDrag();

    this.refresh();
    window.addEventListener('load', () => this.refresh());
    window.addEventListener('resize', () => this.refresh());
    setTimeout(() => this.refresh(), 300);
  }

  get cards() {
    return Array.from(this.querySelectorAll('[data-comfort-story-card]'));
  }

  // Measure how far one card advances, how many are visible, and page count.
  get metrics() {
    const cards = this.cards;
    const step =
      cards.length > 1
        ? Math.abs(cards[1].offsetLeft - cards[0].offsetLeft)
        : cards[0]?.getBoundingClientRect().width || this.track.clientWidth;
    const perView = Math.max(1, Math.round(this.track.clientWidth / step));
    const pageCount = Math.max(1, cards.length - perView + 1);
    return { step, pageCount };
  }

  currentPage() {
    const { step, pageCount } = this.metrics;
    const maxScroll = this.track.scrollWidth - this.track.clientWidth;
    if (this.track.scrollLeft >= maxScroll - 2) return pageCount - 1;
    return Math.max(0, Math.min(Math.round(this.track.scrollLeft / step), pageCount - 1));
  }

  goToPage(index) {
    const { step, pageCount } = this.metrics;
    const clamped = Math.max(0, Math.min(index, pageCount - 1));
    this.track.scrollTo({ left: clamped * step, behavior: 'smooth' });
  }

  // (Re)build the dots to match the number of scroll pages, then refresh state.
  refresh() {
    if (!this.track) return;
    const { pageCount } = this.metrics;

    if (this.controls) this.controls.hidden = pageCount <= 1;

    if (this.dotsWrap && this.dots.length !== pageCount) {
      this.dotsWrap.innerHTML = '';
      this.dots = [];
      for (let i = 0; i < pageCount; i += 1) {
        const dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'product-comfort-stories__dot';
        dot.setAttribute('aria-label', `Go to page ${i + 1}`);
        dot.addEventListener('click', () => this.goToPage(i));
        this.dotsWrap.appendChild(dot);
        this.dots.push(dot);
      }
    }

    this.updateActiveState();
  }

  scheduleUpdate() {
    if (this.rafId) return;
    this.rafId = requestAnimationFrame(() => {
      this.rafId = null;
      this.updateActiveState();
    });
  }

  updateActiveState() {
    const active = this.currentPage();
    this.dots.forEach((dot, i) => {
      if (i === active) dot.setAttribute('aria-current', 'true');
      else dot.removeAttribute('aria-current');
    });
  }

  bindDrag() {
    const track = this.track;
    if (!track) return;

    let isDown = false;
    let startX = 0;
    let scrollLeft = 0;
    let pointerId = null;
    let didDrag = false;

    track.addEventListener('pointerdown', (event) => {
      if (event.pointerType !== 'mouse' || event.button !== 0) return;
      isDown = true;
      didDrag = false;
      startX = event.clientX;
      scrollLeft = track.scrollLeft;
      pointerId = event.pointerId;
    });

    track.addEventListener('pointermove', (event) => {
      if (!isDown) return;
      const delta = event.clientX - startX;
      if (!didDrag && Math.abs(delta) > 5) {
        didDrag = true;
        track.classList.add('is-dragging');
        try {
          track.setPointerCapture(pointerId);
        } catch (error) {
          /* ignore */
        }
      }
      if (didDrag) track.scrollLeft = scrollLeft - delta;
    });

    const stopDragging = () => {
      if (!isDown) return;
      isDown = false;
      track.classList.remove('is-dragging');
      if (pointerId !== null && track.hasPointerCapture(pointerId)) {
        track.releasePointerCapture(pointerId);
      }
      pointerId = null;
    };

    track.addEventListener('pointerup', stopDragging);
    track.addEventListener('pointercancel', stopDragging);
    track.addEventListener('mouseleave', () => {
      isDown = false;
      track.classList.remove('is-dragging');
    });

    track.addEventListener(
      'click',
      (event) => {
        if (!didDrag) return;
        event.preventDefault();
        event.stopPropagation();
        didDrag = false;
      },
      true,
    );
  }
}

if (!customElements.get('product-comfort-stories')) {
  customElements.define('product-comfort-stories', ProductComfortStories);
}
